import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { MdComputer } from "react-icons/md";
import { GiProcessor, GiNetworkBars } from "react-icons/gi";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

const carouselImages = [
  "/renamed_website_images/computer-lab-1.jpg",
  "/renamed_website_images/computer-lab-2.jpg",
];

const featureCards = [
  {
    icon: <MdComputer size={28} />,
    label: "30+ Computers",
    color: "#3b82f6",
    border: "rgba(59,130,246,0.2)",
  },
  {
    icon: <GiProcessor size={28} />,
    label: "Modern CPUs",
    color: "#22c55e",
    border: "rgba(34,197,94,0.2)",
  },
  {
    icon: <GiNetworkBars size={28} />,
    label: "High-Speed Internet",
    color: "#f4c842",
    border: "rgba(244,200,66,0.2)",
  },
];

const ComputerLab = () => {
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);

  const next = () => {
    setDir(1);
    setCurrent((p) => (p + 1) % carouselImages.length);
  };
  const prev = () => {
    setDir(-1);
    setCurrent((p) => (p === 0 ? carouselImages.length - 1 : p - 1));
  };

  return (
    <section className="relative min-h-screen py-24 px-6 md:px-16 pt-36 md:pt-40 overflow-hidden">
      <div
        className="absolute top-0 right-0 w-96 h-96 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(84,204,22,0.06) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <Link
          to="/facilities"
          className="inline-block text-sm font-bold tracking-wide mb-8 transition-colors duration-200"
          style={{ fontFamily: "Nunito, sans-serif", color: "#f4c842" }}
        >
          ← Back to Facilities
        </Link>

        <h1
          className="uppercase mb-4"
          style={{
            fontFamily: "Orbitron, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(1.8rem, 4vw, 3rem)",
            color: "#f0f5ff",
          }}
        >
          Computer Lab
        </h1>
        <div className="cosmic-divider mb-10" />

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {featureCards.map((f, i) => (
            <div
              key={i}
              className="glass-card p-6 flex items-center gap-4 transition-all duration-300 hover:scale-[1.02]"
              style={{ borderColor: f.border }}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: `${f.color}18`,
                  border: `1px solid ${f.border}`,
                  color: f.color,
                }}
              >
                {f.icon}
              </div>
              <h4
                className="font-bold"
                style={{
                  fontFamily: "Cinzel, serif",
                  color: "#f0f5ff",
                  fontSize: "1rem",
                }}
              >
                {f.label}
              </h4>
            </div>
          ))}
        </div>

        {/* Description + carousel */}
        <div className="flex flex-col md:flex-row gap-10 items-center">
          <div className="md:w-1/2">
            <h3
              className="mb-4 font-bold"
              style={{
                fontFamily: "Cinzel, serif",
                color: "#f0f5ff",
                fontSize: "clamp(1rem, 2vw, 1.3rem)",
              }}
            >
              Empowering Students with Technology
            </h3>
            <p
              className="leading-relaxed"
              style={{
                fontFamily: "Nunito, sans-serif",
                color: "#8899b8",
                fontSize: "0.95rem",
              }}
            >
              Our state-of-the-art computer lab introduces students to the
              digital world through hands-on learning. Equipped with modern
              systems, structured internet access, and guided instruction, it
              lays the foundation for digital literacy, coding, and responsible
              internet use.
            </p>
          </div>

          <div
            className="md:w-1/2 relative overflow-hidden rounded-xl h-56"
            style={{ border: "1px solid rgba(84,204,22,0.2)" }}
          >
            <AnimatePresence initial={false} custom={dir}>
              <motion.img
                key={current}
                src={carouselImages[current]}
                alt="Computer Lab"
                className="absolute w-full h-full object-cover"
                custom={dir}
                initial={{ x: dir === 1 ? 300 : -300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: dir === 1 ? -300 : 300, opacity: 0 }}
                transition={{ duration: 0.5 }}
              />
            </AnimatePresence>
            <button
              onClick={prev}
              className="absolute top-1/2 left-2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all duration-200"
              style={{
                background: "rgba(2,8,16,0.8)",
                border: "1px solid rgba(244,200,66,0.3)",
                color: "#f4c842",
              }}
            >
              <IoIosArrowBack />
            </button>
            <button
              onClick={next}
              className="absolute top-1/2 right-2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center z-10 transition-all duration-200"
              style={{
                background: "rgba(2,8,16,0.8)",
                border: "1px solid rgba(244,200,66,0.3)",
                color: "#f4c842",
              }}
            >
              <IoIosArrowForward />
            </button>
          </div>
        </div>

        {/* Bottom Navigation Buttons */}
        <div className="flex items-center justify-between pt-10 mt-12 border-t border-white/10">
          <Link
            to="/facilities/physics-lab"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full glass-card text-xs uppercase tracking-widest text-gold hover:border-gold transition-all"
          >
            <FaArrowLeft /> Previous: Physics Lab
          </Link>
          <Link
            to="/facilities/smart-class"
            className="flex items-center gap-2 px-5 py-2.5 rounded-full cosmic-btn-gold text-xs uppercase tracking-widest"
          >
            Next: Smart Classes <FaArrowRight />
          </Link>
        </div>
      </div>

      <Link
        to="/facilities/physics-lab"
        aria-label="Previous: Physics Lab"
        className="fixed z-50 left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowLeft />
      </Link>
      <Link
        to="/facilities/smart-class"
        aria-label="Next: Smart Classes"
        className="fixed z-50 right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full items-center justify-center flex transition-all duration-300 shadow-xl"
        style={{
          background: "rgba(2,8,16,0.85)",
          border: "1px solid rgba(244,200,66,0.3)",
          color: "#f4c842",
          backdropFilter: "blur(8px)",
        }}
      >
        <FaArrowRight />
      </Link>
    </section>
  );
};

export default ComputerLab;
